/**
 * Linha de um requisito de documento no Step 4.
 *
 * O design pede que o botão "Anexar" vire "Ver arquivo" assim que o upload
 * termina, com um check à esquerda do rótulo — o estado do anexo é a informação
 * principal da linha. Por isso o componente é dirigido por `uploaded`: sem
 * anexo mostra o input, com anexo mostra nome, tamanho, o link de preview e o
 * "Remover" que a spec §10.2 decidiu oferecer.
 *
 * O `input` de arquivo fica escondido atrás do botão (padrão do MUI) e é
 * reiniciado depois de cada escolha, para reanexar o mesmo arquivo continuar
 * disparando `onChange`.
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CircularProgress } from "@mui/material";
import { ChangeEvent, useRef } from "react";
import styled from "styled-components";

import { Button, Caption, Row, Stack } from "../styled";
import { COLORS } from "../tokens";
import { ACCEPTED_DOCUMENT_MIME_TYPES, IUploadedDocument } from "../types";

const ACCEPTED_INPUT_TYPES = ACCEPTED_DOCUMENT_MIME_TYPES.join(",");

const BYTES_PER_KILOBYTE = 1024;

const HiddenInput = styled.input`
  display: none;
`;

const Line = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${COLORS.border};

  &:last-of-type {
    border-bottom: none;
  }
`;

const Label = styled.span<{ $done?: boolean }>`
  color: ${COLORS.ink};
  font-size: 14px;
  font-weight: ${({ $done }) => ($done ? 500 : 400)};
  line-height: 150%;
`;

const RequiredMark = styled.span`
  color: ${COLORS.error};
`;

const FileMeta = styled(Caption)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
`;

const ErrorText = styled(Caption)`
  color: ${COLORS.error};
`;

const Actions = styled(Row)`
  width: auto;
  flex-shrink: 0;
`;

const DoneIcon = styled(CheckCircleIcon)`
  && {
    color: ${COLORS.success};
    font-size: 18px;
  }
`;

/** "1.4 MB" / "820 KB" — só para o titular reconhecer o arquivo que anexou. */
function formatFileSize(bytes: number): string {
  const kilobytes = bytes / BYTES_PER_KILOBYTE;

  if (kilobytes < BYTES_PER_KILOBYTE) return `${Math.round(kilobytes)} KB`;

  return `${(kilobytes / BYTES_PER_KILOBYTE).toFixed(1)} MB`;
}

interface IProps {
  label: string;
  required: boolean;
  /** Anexo já enviado para este requisito, se houver. */
  uploaded?: IUploadedDocument;
  isUploading: boolean;
  /** Mensagem da última falha de upload desta linha. */
  errorMessage?: string;
  onSelectFile: (file: File) => void;
  onPreview: () => void;
  /** Desanexa o arquivo já enviado, voltando a linha ao estado "Anexar". */
  onRemove: () => void;
}

export function DocumentRequirementRow({
  label,
  required,
  uploaded,
  isUploading,
  errorMessage,
  onSelectFile,
  onPreview,
  onRemove,
}: IProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    /** Zerar o valor permite reanexar o mesmo arquivo depois de um erro. */
    event.target.value = "";

    if (file) onSelectFile(file);
  }

  return (
    <Line>
      <Stack $gap="2px">
        <Row $gap="6px">
          {!!uploaded && <DoneIcon />}
          <Label $done={!!uploaded}>
            {label}
            {required && <RequiredMark> *</RequiredMark>}
          </Label>
        </Row>

        {!!uploaded && (
          <FileMeta>
            {uploaded.fileName} · {formatFileSize(uploaded.sizeBytes)}
          </FileMeta>
        )}

        {!!errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </Stack>

      <Actions
        $gap="8px"
        $justify="flex-end"
      >
        {isUploading && (
          <CircularProgress
            size={20}
            aria-label={`Enviando ${label}`}
          />
        )}

        {!isUploading && !!uploaded && (
          <Button
            variant="text"
            onClick={onPreview}
          >
            Ver arquivo
          </Button>
        )}

        {!isUploading && (
          <Button
            variant={uploaded ? "text" : "outlined"}
            onClick={() => inputRef.current?.click()}
          >
            {uploaded ? "Substituir" : "Anexar"}
          </Button>
        )}

        {!isUploading && !!uploaded && (
          <Button
            variant="text"
            color="error"
            onClick={onRemove}
          >
            Remover
          </Button>
        )}

        <HiddenInput
          ref={inputRef}
          type="file"
          accept={ACCEPTED_INPUT_TYPES}
          onChange={handleChange}
        />
      </Actions>
    </Line>
  );
}
